import { FC, MouseEvent, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Avatar, Colors, ExternalLink, FInter, useBreakpoint } from '@brg/ui';
import { Project } from '@brg/api';
import { ProfileUtils } from 'core/auth';
import { ProjectDesignerName } from 'shared/components/projectDesignerName';
import { AuthorMobile } from 'shared/components/projectCard/projectCardLayoutNew';
import { computeDesigner } from 'shared/helpers/computeData';
import { getProjectAiSummary } from 'shared/helpers/getProjectAiSummary';
import stripHtml from 'shared/helpers/stripHtml';
import { executeTypografValue } from 'shared/utils/executeTypograf';
import { IDesignerLight } from 'shared/common';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { LayoutConst } from '../../../../core';
import {
  getOtherProjectsMetaPlan,
  getProjectCoverViewType,
  isProLargeLandingCover,
} from './otherProjectsMetaPlan';

const META_TEXT_CLAMP_LINES = 10;

const metaTextBase = css`
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-style: normal;
`;

const clampMultiline = css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${META_TEXT_CLAMP_LINES};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  text-align: left;
`;

const MetaAuthor = styled(FInter.SmallText)`
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`;

const projectTitleTextCss = css<{ $hasBelow: boolean; $hasAuthorPlainBelow: boolean }>`
  ${metaTextBase}
  color: ${({ theme }) => theme.projectView.titleColor};
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '8px' : '0')};

  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: ${({ $hasBelow, $hasAuthorPlainBelow }) => {
      if ($hasAuthorPlainBelow) {
        return '0';
      }
      return $hasBelow ? '8px' : '0';
    }};
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.24px;
  }
`;

const ProjectTitleLink = styled(ExternalLink)<{
  $hasBelow: boolean;
  $hasAuthorPlainBelow: boolean;
}>`
  ${projectTitleTextCss}
  display: block;
  text-decoration: none;
  cursor: pointer;
`;

const AiSummaryText = styled.div<{ $hasBelow: boolean }>`
  ${metaTextBase}
  ${clampMultiline}
  color: ${({ theme }) => theme.projectView.titleColor};
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '14px' : '0')};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.24px;
  }
`;

const AuthorPlainRow = styled.div<{ $hasBelow: boolean }>`
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '8px' : '0')};
`;

const AuthorRow = styled.div<{ $hasAnnotationBelow: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ $hasAnnotationBelow }) => ($hasAnnotationBelow ? '8px' : '0')};
`;

const AnnotationAuthorAvatarSlot = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  overflow: hidden;
`;

const AuthorName = styled.span`
  ${metaTextBase}
  color: ${Colors.galBlack47};
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    letter-spacing: 0;
  }
`;

const AuthorProfileLink = styled(ExternalLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover ${AuthorName} {
    color: ${({ theme }) => theme.projectView.authorColorHover};
  }
`;

const AnnotationText = styled.div`
  ${metaTextBase}
  ${clampMultiline}
  color: ${Colors.galBlack47};
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    letter-spacing: 0;
  }
`;

const getAnnotationText = (project: Project, language: string): string => {
  const { annotationText, annotationText_en } = project?.additionalInfo || {};
  const raw =
    language === 'en'
      ? annotationText_en || annotationText
      : annotationText || annotationText_en;
  const html = executeTypografValue(raw) || '';
  return stripHtml(html.replace(/<br\s*\/?>/gi, ' '))
    .replace(/\s+/g, ' ')
    .trim();
};

const getProjectTitleText = (project: Project): string => {
  const fromAdditional = project?.additionalContent?.title?.text;
  if (fromAdditional) {
    return String(fromAdditional).trim();
  }
  return (executeTypografValue(project?.title) || '').trim();
};

export const OtherProjectsCardMeta: FC<{
  project: Project;
  language: string;
  viewType?: string;
}> = ({ project, language, viewType: viewTypeProp }) => {
  const theme = useTheme();
  const isCompactViewport = useBreakpoint(LayoutConst.screen.l);

  const coverViewType = useMemo(
    () => viewTypeProp || getProjectCoverViewType(project),
    [project, viewTypeProp],
  );

  const isProLargeLanding = useMemo(() => isProLargeLandingCover(project), [project]);

  const projectTitle = useMemo(() => getProjectTitleText(project), [project]);

  const aiSummaryText = useMemo(
    () =>
      getProjectAiSummary(
        (project as { aiSummary?: unknown })?.aiSummary,
        language,
      ),
    [project, language],
  );

  const annotation = useMemo(
    () => getAnnotationText(project, language),
    [project, language],
  );

  const designers = useMemo((): IDesignerLight[] => {
    const author = project?.author;
    const coAuthors = project?.coAuthors;
    if (author) {
      return [computeDesigner(author), ...(coAuthors?.map(computeDesigner) ?? [])];
    }
    return coAuthors?.map(computeDesigner) ?? [];
  }, [project]);

  const isMultipleAuthors = designers.length > 1;

  const authorName = useMemo(() => {
    if (isMultipleAuthors) return '';
    const author = project?.author;
    if (!author) return '';
    return ProfileUtils.getFullName(author).filter(Boolean).join(' ').trim();
  }, [project, isMultipleAuthors]);

  const authorAvatarUrl = useMemo(
    () => (isMultipleAuthors ? undefined : ProfileUtils.getAvatarURLOrUndefined(project?.author)),
    [project, isMultipleAuthors],
  );

  const singleAuthorProfileUrl = useMemo(() => {
    if (isMultipleAuthors || designers.length === 0) return '';
    return CreateLinkTo.designerNew(designers[0]);
  }, [designers, isMultipleAuthors]);

  const projectPageUrl = useMemo(() => CreateLinkTo.projectNew(project), [project]);

  const stopCardNavigation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const hasAiSummary = Boolean(aiSummaryText);
  const hasAuthors = designers.length > 0;
  const hasAnnotation = Boolean(annotation);

  const plan = useMemo(
    () =>
      getOtherProjectsMetaPlan({
        viewType: coverViewType,
        isCompactViewport,
        isProLargeLanding,
        hasAiSummary,
        hasAnnotation,
        hasAuthors,
      }),
    [
      coverViewType,
      isCompactViewport,
      isProLargeLanding,
      hasAiSummary,
      hasAnnotation,
      hasAuthors,
    ],
  );

  if (!plan) {
    return null;
  }

  const showTitle = plan.showTitle && Boolean(projectTitle);
  const showAuthorPlain = plan.showAuthorPlain && hasAuthors;
  const showSummary = plan.showSummary && hasAiSummary;
  const showAnnotationAuthor = plan.showAnnotationAuthor && hasAuthors;
  const showAnnotationText = plan.showAnnotationText && hasAnnotation;

  const hasBelowTitle =
    showAuthorPlain ||
    showSummary ||
    showAnnotationAuthor ||
    showAnnotationText;
  const hasBelowAuthorPlain = showSummary || showAnnotationAuthor || showAnnotationText;
  const hasBelowSummary = showAnnotationAuthor || showAnnotationText;

  if (
    !showTitle &&
    !showAuthorPlain &&
    !showSummary &&
    !showAnnotationAuthor &&
    !showAnnotationText
  ) {
    return null;
  }

  const renderAuthorPlain = () => {
    if (!showAuthorPlain) return null;

    if (isMultipleAuthors) {
      return (
        <AuthorPlainRow
          $hasBelow={hasBelowAuthorPlain}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <AuthorMobile hasSubtitle={false} style={{ marginTop: 0, marginBottom: 0 }}>
            <MetaAuthor>
              <ProjectDesignerName value={designers} multipleAuthorsFit={false} />
            </MetaAuthor>
          </AuthorMobile>
        </AuthorPlainRow>
      );
    }

    return (
      <AuthorPlainRow
        $hasBelow={hasBelowAuthorPlain}
        onClick={stopCardNavigation}
        onMouseDown={stopCardNavigation}
      >
        <AuthorProfileLink
          to={singleAuthorProfileUrl}
          external
          target="_self"
          onClick={stopCardNavigation}
          onMouseDown={stopCardNavigation}
        >
          <AuthorName>{authorName}</AuthorName>
        </AuthorProfileLink>
      </AuthorPlainRow>
    );
  };

  const renderAnnotationAuthor = () => {
    if (!showAnnotationAuthor) return null;

    if (isMultipleAuthors) {
      return (
        <AuthorMobile
          hasSubtitle={false}
          style={{ marginTop: 0, marginBottom: showAnnotationText ? 8 : 0 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MetaAuthor>
            <ProjectDesignerName value={designers} multipleAuthorsFit={false} />
          </MetaAuthor>
        </AuthorMobile>
      );
    }

    return (
      <AuthorProfileLink
        to={singleAuthorProfileUrl}
        external
        target="_self"
        onClick={stopCardNavigation}
        onMouseDown={stopCardNavigation}
      >
        <AuthorRow $hasAnnotationBelow={showAnnotationText} as="span">
          <AnnotationAuthorAvatarSlot>
            <Avatar
              imgBackColor={theme.app.backgroundColor}
              {...designers[0]}
              avatar={authorAvatarUrl}
              fs={10}
            />
          </AnnotationAuthorAvatarSlot>
          <AuthorName>{authorName}</AuthorName>
        </AuthorRow>
      </AuthorProfileLink>
    );
  };

  return (
    <MetaWrapper>
      {showTitle && (
        <ProjectTitleLink
          to={projectPageUrl}
          external
          target="_self"
          $hasBelow={hasBelowTitle}
          $hasAuthorPlainBelow={showAuthorPlain}
          onClick={stopCardNavigation}
          onMouseDown={stopCardNavigation}
        >
          {projectTitle}
        </ProjectTitleLink>
      )}
      {renderAuthorPlain()}
      {showSummary && (
        <AiSummaryText $hasBelow={hasBelowSummary}>{aiSummaryText}</AiSummaryText>
      )}
      {renderAnnotationAuthor()}
      {showAnnotationText && <AnnotationText>{annotation}</AnnotationText>}
    </MetaWrapper>
  );
};
import { FC, MouseEvent, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Avatar, Colors, ExternalLink, FInter, useBreakpoint } from '@brg/ui';
import { Project } from '@brg/api';
import { ProfileUtils } from 'core/auth';
import { ProjectDesignerName } from 'shared/components/projectDesignerName';
import { AuthorMobile } from 'shared/components/projectCard/projectCardLayoutNew';
import { computeDesigner } from 'shared/helpers/computeData';
import { getProjectAiSummary } from 'shared/helpers/getProjectAiSummary';
import stripHtml from 'shared/helpers/stripHtml';
import { executeTypografValue } from 'shared/utils/executeTypograf';
import { IDesignerLight } from 'shared/common';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { LayoutConst } from '../../../../core';
import {
  getOtherProjectsMetaPlan,
  getProjectCoverViewType,
  isProLargeLandingCover,
} from './otherProjectsMetaPlan';

const META_TEXT_CLAMP_LINES = 10;

const metaTextBase = css`
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Inter, sans-serif;
  font-style: normal;
`;

const clampMultiline = css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${META_TEXT_CLAMP_LINES};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  text-align: left;
`;

const MetaAuthor = styled(FInter.SmallText)`
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`;

const projectTitleTextCss = css<{ $hasBelow: boolean; $hasAuthorPlainBelow: boolean }>`
  ${metaTextBase}
  color: ${({ theme }) => theme.projectView.titleColor};
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '8px' : '0')};

  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: ${({ $hasBelow, $hasAuthorPlainBelow }) => {
      if ($hasAuthorPlainBelow) {
        return '0';
      }
      return $hasBelow ? '8px' : '0';
    }};
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.24px;
  }
`;

const ProjectTitleLink = styled(ExternalLink)<{
  $hasBelow: boolean;
  $hasAuthorPlainBelow: boolean;
}>`
  ${projectTitleTextCss}
  display: block;
  text-decoration: none;
  cursor: pointer;
`;

const AiSummaryText = styled.div<{ $hasBelow: boolean }>`
  ${metaTextBase}
  ${clampMultiline}
  color: ${({ theme }) => theme.projectView.titleColor};
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '14px' : '0')};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.24px;
  }
`;

const AuthorPlainRow = styled.div<{ $hasBelow: boolean }>`
  margin-bottom: ${({ $hasBelow }) => ($hasBelow ? '8px' : '0')};
`;

const AuthorRow = styled.div<{ $hasAnnotationBelow: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ $hasAnnotationBelow }) => ($hasAnnotationBelow ? '8px' : '0')};
`;

const AnnotationAuthorAvatarSlot = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  overflow: hidden;
`;

const AuthorName = styled.span`
  ${metaTextBase}
  color: ${Colors.galBlack47};
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    letter-spacing: 0;
  }
`;

const AuthorProfileLink = styled(ExternalLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover ${AuthorName} {
    color: ${({ theme }) => theme.projectView.authorColorHover};
  }
`;

const AnnotationText = styled.div`
  ${metaTextBase}
  ${clampMultiline}
  color: ${Colors.galBlack47};
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    letter-spacing: 0;
  }
`;

const getAnnotationText = (project: Project, language: string): string => {
  const { annotationText, annotationText_en } = project?.additionalInfo || {};
  const raw =
    language === 'en'
      ? annotationText_en || annotationText
      : annotationText || annotationText_en;
  const html = executeTypografValue(raw) || '';
  return stripHtml(html.replace(/<br\s*\/?>/gi, ' '))
    .replace(/\s+/g, ' ')
    .trim();
};

const getProjectTitleText = (project: Project): string => {
  const fromAdditional = project?.additionalContent?.title?.text;
  if (fromAdditional) {
    return String(fromAdditional).trim();
  }
  return (executeTypografValue(project?.title) || '').trim();
};

export const OtherProjectsCardMeta: FC<{
  project: Project;
  language: string;
  viewType?: string;
}> = ({ project, language, viewType: viewTypeProp }) => {
  const theme = useTheme();
  const isCompactViewport = useBreakpoint(LayoutConst.screen.l);

  const coverViewType = useMemo(
    () => viewTypeProp || getProjectCoverViewType(project),
    [project, viewTypeProp],
  );

  const isProLargeLanding = useMemo(() => isProLargeLandingCover(project), [project]);

  const projectTitle = useMemo(() => getProjectTitleText(project), [project]);

  const aiSummaryText = useMemo(
    () =>
      getProjectAiSummary(
        (project as { aiSummary?: unknown })?.aiSummary,
        language,
      ),
    [project, language],
  );

  const annotation = useMemo(
    () => getAnnotationText(project, language),
    [project, language],
  );

  const designers = useMemo((): IDesignerLight[] => {
    const author = project?.author;
    const coAuthors = project?.coAuthors;
    if (author) {
      return [computeDesigner(author), ...(coAuthors?.map(computeDesigner) ?? [])];
    }
    return coAuthors?.map(computeDesigner) ?? [];
  }, [project]);

  const isMultipleAuthors = designers.length > 1;

  const authorName = useMemo(() => {
    if (isMultipleAuthors) return '';
    const author = project?.author;
    if (!author) return '';
    return ProfileUtils.getFullName(author).filter(Boolean).join(' ').trim();
  }, [project, isMultipleAuthors]);

  const authorAvatarUrl = useMemo(
    () => (isMultipleAuthors ? undefined : ProfileUtils.getAvatarURLOrUndefined(project?.author)),
    [project, isMultipleAuthors],
  );

  const singleAuthorProfileUrl = useMemo(() => {
    if (isMultipleAuthors || designers.length === 0) return '';
    return CreateLinkTo.designerNew(designers[0]);
  }, [designers, isMultipleAuthors]);

  const projectPageUrl = useMemo(() => CreateLinkTo.projectNew(project), [project]);

  const stopCardNavigation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const hasAiSummary = Boolean(aiSummaryText);
  const hasAuthors = designers.length > 0;
  const hasAnnotation = Boolean(annotation);

  const plan = useMemo(
    () =>
      getOtherProjectsMetaPlan({
        viewType: coverViewType,
        isCompactViewport,
        isProLargeLanding,
        hasAiSummary,
        hasAnnotation,
        hasAuthors,
      }),
    [
      coverViewType,
      isCompactViewport,
      isProLargeLanding,
      hasAiSummary,
      hasAnnotation,
      hasAuthors,
    ],
  );

  if (!plan) {
    return null;
  }

  const showTitle = plan.showTitle && Boolean(projectTitle);
  const showAuthorPlain = plan.showAuthorPlain && hasAuthors;
  const showSummary = plan.showSummary && hasAiSummary;
  const showAnnotationAuthor = plan.showAnnotationAuthor && hasAuthors;
  const showAnnotationText = plan.showAnnotationText && hasAnnotation;

  const hasBelowTitle =
    showAuthorPlain ||
    showSummary ||
    showAnnotationAuthor ||
    showAnnotationText;
  const hasBelowAuthorPlain = showSummary || showAnnotationAuthor || showAnnotationText;
  const hasBelowSummary = showAnnotationAuthor || showAnnotationText;

  if (
    !showTitle &&
    !showAuthorPlain &&
    !showSummary &&
    !showAnnotationAuthor &&
    !showAnnotationText
  ) {
    return null;
  }

  const renderAuthorPlain = () => {
    if (!showAuthorPlain) return null;

    if (isMultipleAuthors) {
      return (
        <AuthorPlainRow
          $hasBelow={hasBelowAuthorPlain}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <AuthorMobile hasSubtitle={false} style={{ marginTop: 0, marginBottom: 0 }}>
            <MetaAuthor>
              <ProjectDesignerName value={designers} multipleAuthorsFit={false} />
            </MetaAuthor>
          </AuthorMobile>
        </AuthorPlainRow>
      );
    }

    return (
      <AuthorPlainRow
        $hasBelow={hasBelowAuthorPlain}
        onClick={stopCardNavigation}
        onMouseDown={stopCardNavigation}
      >
        <AuthorProfileLink
          to={singleAuthorProfileUrl}
          external
          target="_self"
          onClick={stopCardNavigation}
          onMouseDown={stopCardNavigation}
        >
          <AuthorName>{authorName}</AuthorName>
        </AuthorProfileLink>
      </AuthorPlainRow>
    );
  };

  const renderAnnotationAuthor = () => {
    if (!showAnnotationAuthor) return null;

    if (isMultipleAuthors) {
      return (
        <AuthorMobile
          hasSubtitle={false}
          style={{ marginTop: 0, marginBottom: showAnnotationText ? 8 : 0 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MetaAuthor>
            <ProjectDesignerName value={designers} multipleAuthorsFit={false} />
          </MetaAuthor>
        </AuthorMobile>
      );
    }

    return (
      <AuthorProfileLink
        to={singleAuthorProfileUrl}
        external
        target="_self"
        onClick={stopCardNavigation}
        onMouseDown={stopCardNavigation}
      >
        <AuthorRow $hasAnnotationBelow={showAnnotationText} as="span">
          <AnnotationAuthorAvatarSlot>
            <Avatar
              imgBackColor={theme.app.backgroundColor}
              {...designers[0]}
              avatar={authorAvatarUrl}
              fs={10}
            />
          </AnnotationAuthorAvatarSlot>
          <AuthorName>{authorName}</AuthorName>
        </AuthorRow>
      </AuthorProfileLink>
    );
  };

  return (
    <MetaWrapper>
      {showTitle && (
        <ProjectTitleLink
          to={projectPageUrl}
          external
          target="_self"
          $hasBelow={hasBelowTitle}
          $hasAuthorPlainBelow={showAuthorPlain}
          onClick={stopCardNavigation}
          onMouseDown={stopCardNavigation}
        >
          {projectTitle}
        </ProjectTitleLink>
      )}
      {renderAuthorPlain()}
      {showSummary && (
        <AiSummaryText $hasBelow={hasBelowSummary}>{aiSummaryText}</AiSummaryText>
      )}
      {renderAnnotationAuthor()}
      {showAnnotationText && <AnnotationText>{annotation}</AnnotationText>}
    </MetaWrapper>
  );
};
